import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import _ from 'lodash';
import {
    FilterField,
    FilterFieldId,
    FilterSurveyJobChange
} from 'src/app/feature/map-viewer/map-menus/shared/job-filter/job-filter.types';
import { ButtonType } from 'src/app/shared/common/components/buttons/button';
import { JobFilterStreamsService } from 'src/app/shared/common/job-filter/job-filter-streams.service';
import { SurveyJobFilterId } from 'src/app/shared/survey-data-services/survey-job';

@Component({
    selector: 'job-filter',
    templateUrl: './job-filter.component.html'
})
export class JobFilterComponent<T> implements OnInit, OnChanges {
    @Input() filterTitle: string = '';
    @Input() fields: FilterField[] = [];
    @Input() values: T;
    @Output() valuesChange = new EventEmitter<FilterSurveyJobChange<T>>();
    @Output() closeFilterPanel = new EventEmitter<void>();

    isFilterApplied = false;
    internalValues: T & { [key in FilterFieldId]?: any };
    public ButtonType = ButtonType;

    ngOnInit() {
        this.internalValues = _.cloneDeep(this.values);
        this.updateAppliedCount();
        this.updateDateFieldConstraints();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (
            changes.values &&
            changes.values.currentValue &&
            !_.isEqual(this.internalValues, changes.values.currentValue)
        ) {
            this.internalValues = _.cloneDeep(changes.values.currentValue);
            this.updateAppliedCount();
            //clear date constraints
            this.updateDateFieldConstraints();
        }
    }

    onFieldChange(id: SurveyJobFilterId, value: any) {
        this.internalValues = { ...this.internalValues, [id]: value };
        const appliedCount = this.updateAppliedCount();
        const change: FilterSurveyJobChange<T> = {
            value: this.internalValues,
            fieldChanged: id,
            clear: false,
            filterAppliedCount: appliedCount
        };

        this.valuesChange.emit(change);

        const field = this.findField(this.fields, id);

        if (field?.execute) {
            field.execute(value, field);
        }
        if (field && field.type === 'date') {
            this.updateDateFieldConstraints(id);
        }
    }

    addChip(fieldId: FilterFieldId, chip: string) {
        this.onFieldChange(fieldId, [...(this.internalValues[fieldId] || []), chip]);
    }

    removeChipFromField(fieldId: FilterFieldId, chipToRemove: string) {
        const filteredChips = (this.internalValues[fieldId] || []).filter((chip: string) => chip !== chipToRemove);
        this.onFieldChange(fieldId, filteredChips);
    }

    clear() {
        if (this.isFilterApplied) {
            // Reset all values to initial values
            this.internalValues = JobFilterStreamsService.getDefaultValues(this.fields) as T;
            this.isFilterApplied = false; // Reset before emitting for synchronous UI update
            this.valuesChange.emit({
                value: this.internalValues,
                fieldChanged: null,
                clear: true,
                filterAppliedCount: 0
            });
            //clear date constraints
            this.updateDateFieldConstraints();
        }
    }

    close() {
        this.closeFilterPanel.emit();
    }

    private updateAppliedCount(): number {
        const appliedCount = this.internalValues && JobFilterStreamsService.filterAppliedCount<T>(this.internalValues);
        this.isFilterApplied = appliedCount > 0;
        return appliedCount;
    }

    /**
     * Initializes date fields with constraints based on the current values.
     * This is called on initialization and when a date field changes.
     */
    private updateDateFieldConstraints(id?: FilterFieldId): void {
        this.fields = this.fields.map(groupField => {
            if (groupField.type === 'group' && Array.isArray(groupField.fields)) {
                const dateFields = groupField.fields.filter(f => f.type === 'date');
                if (dateFields.length === 2) {
                    const [fromField, toField] = dateFields;
                    const fromValue = this.internalValues[fromField.id];
                    const toValue = this.internalValues[toField.id];
                    // Only update the group if initializing or if the changed field is in this group
                    if (!id || groupField.fields.some(f => f.id === id)) {
                        return {
                            ...groupField,
                            fields: groupField.fields.map(f => {
                                if (f.type !== 'date') return f;
                                // Initialization: set both constraints
                                if (!id) {
                                    if (f.id === toField.id) return { ...f, minDate: fromValue || null };
                                    if (f.id === fromField.id) return { ...f, maxDate: toValue || null };
                                    return f;
                                }
                                // Only update the paired field
                                if (id === toField.id && f.id === fromField.id) {
                                    return { ...f, maxDate: toValue || null };
                                }
                                if (id === fromField.id && f.id === toField.id) {
                                    return { ...f, minDate: fromValue || null };
                                }
                                return f;
                            })
                        };
                    }
                }
            }
            return groupField;
        });
    }

    private findField(fields: FilterField[], id: FilterFieldId): FilterField | undefined {
        for (const field of fields) {
            if (field.type !== 'group' && field.id === id) return field;
            if (field.type === 'group' && field.fields) {
                const found = this.findField(field.fields, id);
                if (found) return found;
            }
        }
        return undefined;
    }
}
